import {
    ClusterMetricCell,
    GrowthMetricCell,
    HealthStatMetricCell,
} from 'components/HealthStatMetricCell';
import {
    GOOGLE_METADATA_BY_KEY,
    INSTAGRAM_METADATA_BY_KEY,
    POPULARITY_METADATA_BY_KEY,
    TIKTOK_METADATA_BY_KEY,
} from 'utils/metrics';
import { DATA_SOURCES } from '@/config/constants';

/**
 * The stat row above the time series, and inside the explore drawers: volume,
 * YoY, an optional predicted YoY, the cluster.
 */

// `clusterDataSource` is ClusterTooltip's copy key, not the descriptor's apiName:
// it knows 'popularity' where the descriptor says 'pop_index'.
const HERO_SPEC = {
    [DATA_SOURCES.GOOGLE]: {
        volume: {
            stat: 'average monthly searches',
            label: 'Avg Monthly Searches',
        },
        growth: { metric: GOOGLE_METADATA_BY_KEY.YoY, dataKey: 'YoY' },
        predicted: { stat: 'predicted growth', label: 'Predicted YoY' },
        clusterDataSource: 'google',
    },
    [DATA_SOURCES.TIKTOK]: {
        volume: { stat: 'average weekly views', label: 'Avg Weekly Views' },
        growth: {
            metric: TIKTOK_METADATA_BY_KEY.non_cum_YoY_rolling,
            dataKey: 'non_cum_YoY_rolling',
        },
        clusterDataSource: 'tiktok',
    },
    [DATA_SOURCES.INSTAGRAM]: {
        volume: { stat: 'average weekly likes', label: 'Avg Weekly Likes' },
        growth: {
            metric: INSTAGRAM_METADATA_BY_KEY.non_cum_YoY_rolling,
            dataKey: 'non_cum_YoY_rolling',
        },
        clusterDataSource: 'instagram',
    },
    [DATA_SOURCES.POPULARITY]: {
        // Formats off `data` rather than reading `healthStats`: the formatters
        // return an ASCII '-' where the healthStats path gives an em dash.
        volume: {
            stat: 'average monthly popularity',
            label: 'Popularity',
            metric: POPULARITY_METADATA_BY_KEY.popularity_index_avg_12_month,
            dataKey: 'popularity_index_avg_12_month',
        },
        growth: { metric: POPULARITY_METADATA_BY_KEY.YoY, dataKey: 'YoY' },
        // Composes its predicted label rather than replacing it.
        predicted: {
            stat: 'popularity predicted growth',
            label: 'Predicted YoY',
            labelSuffix: ' Predicted YoY',
            metric: POPULARITY_METADATA_BY_KEY.predicted_YoY,
            dataKey: 'predicted_YoY',
        },
        clusterDataSource: 'popularity',
        alwaysPredicted: true,
    },
};

// Four cells need explicit ratios; three divide evenly.
const FOUR_COLS =
    'grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1.2fr)]';

const valueOf = (cell, data, healthStats) =>
    cell.metric
        ? (cell.metric.format(data?.[cell.dataKey]) ?? '—')
        : (healthStats?.[cell.stat]?.metric ?? '—');

// `showPredicted` is a prop because Google hides that cell in TikTok markets —
// a per-render decision the page owns. `layout="wrap"` is the drawer's 2x2.

function MetricHeroCard({
    source,
    data,
    healthStats,
    cluster,
    isLoading = false,
    showPredicted = false,
    layout = 'row',
    valueTextSize = 'sm',
}) {
    const spec = HERO_SPEC[source];
    if (!spec) return null;

    const wrap = layout === 'wrap';
    const predicted =
        !!spec.predicted && (wrap || spec.alwaysPredicted || showPredicted);

    const volumeCell = (
        <HealthStatMetricCell
            value={valueOf(spec.volume, data, healthStats)}
            defaultLabel={spec.volume.label}
            healthStatKey={spec.volume.stat}
            healthStats={healthStats}
            valueTextSize={valueTextSize}
            isLoading={isLoading}
        />
    );
    const growthCell = (
        <GrowthMetricCell
            metric={spec.growth.metric}
            value={data?.[spec.growth.dataKey]}
            valueTextSize={valueTextSize}
            isLoading={isLoading}
            // In the 2x2 it is the last cell of its row.
            hasBorder={!wrap}
        />
    );
    const predictedCell = predicted ? (
        <HealthStatMetricCell
            value={valueOf(spec.predicted, data, healthStats)}
            defaultLabel={spec.predicted.label}
            labelSuffix={spec.predicted.labelSuffix}
            healthStatKey={spec.predicted.stat}
            healthStats={healthStats}
            valueTextSize={valueTextSize}
            isLoading={isLoading}
        />
    ) : null;
    const clusterCell = (
        <ClusterMetricCell
            cluster={cluster}
            dataSource={spec.clusterDataSource}
            isLoading={isLoading}
        />
    );

    if (wrap) {
        return (
            <>
                <div className="grid grid-cols-2">
                    {volumeCell}
                    {growthCell}
                </div>
                <div className="grid grid-cols-2 border-t border-border">
                    {predictedCell}
                    {clusterCell}
                </div>
            </>
        );
    }

    return (
        <div className={`grid ${predicted ? FOUR_COLS : 'grid-cols-3'}`}>
            {volumeCell}
            {growthCell}
            {predictedCell}
            {clusterCell}
        </div>
    );
}

export default MetricHeroCard;
